import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useQuery, useLazyQuery } from '@apollo/client';
import { ECHEMICAL_QUERY, MATERIALS_DATA_QUERY, SAMPLES_BY_MATID_QUERY, XPS_QUERY, XRD_QUERY, SAMPLES_BY_PUBID_QUERY, SAMPLES_QUERY } from './query';
import { ChevronDown } from "lucide-react";
import MaterialsTable from './MaterialTable';
import { FaBook, FaCode, FaComments, FaGithub, FaCreativeCommons } from 'react-icons/fa';
import GenericGraph from './GenericGraph';
import periodicTableData from './periodicTableData';
import Header from './Header';
import AdvancedTable from './AdvanceTable';
import AdvancedDataGrid from './AdvanceDataGrid';
import AdvanceMaterialTable from './AdvanceMaterialTable';

const Input = (props) => (
  <input {...props} className="shadow appearance-none border rounded w-full py-2 px-3 text-gray-700 leading-tight focus:outline-none focus:shadow-outline" />
);

const Button = ({ children, ...props }) => (
  <button {...props} className="px-4 py-2 bg-red-800 text-white rounded hover:bg-red-800 transition-colors">
    {children}
  </button>
);



const ExploreMaterialsSearch = () => {
  // State declarations
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedElements, setSelectedElements] = useState([]);
  const [searchMode, setSearchMode] = useState('Only Elements');
  const [filters, setFilters] = useState({ space_group: '', reaction: ''});
  const [xrdData, setXrdData] = useState([]);
  const [xpsData, setXpsData] = useState([]);
  const [cvInitialData, setCvInitialData] = useState([]);
  const [cvEndData, setCvEndData] = useState([]);
  const [selectedRowIndices, setSelectedRowIndices] = useState([0]);
  
  const [isPeriodicTableVisible, setIsPeriodicTableVisible] = useState(true);
  const [isAnimating, setIsAnimating] = useState(false);

  // GraphQL queries
  
  const [getXrdData] = useLazyQuery(XRD_QUERY);
  const [getXpsData] = useLazyQuery(XPS_QUERY);
  const [getEchemicalData] = useLazyQuery(ECHEMICAL_QUERY);

  // Utility functions
  const selectIndices = useCallback((array, indices) => indices.map(index => array[index]), []);

  // Toggle periodic table visibility
  const togglePeriodicTable = () => {
    setIsAnimating(true);
    setIsPeriodicTableVisible(!isPeriodicTableVisible);
    setTimeout(() => setIsAnimating(false), 300);
  };
  

  // Derived state
  // const materials = useMemo(() => data?.materials?.edges.map(edge => edge.node) || [], [data]);
  const defaultVisibleColumns = ['composition','DOI', 'morphology', 'reaction', 'onset_potential_(+/-100_\u03BCA/cm2)', 'onset_potential_(+/-1000_\u03BCA/cm2)', 'pH', 'replicate', 'onset_potential_mean_std(+/-0.1_mA/cm2)'];
  
  const { loading, error, data:sampleData } = useQuery(SAMPLES_QUERY);
  const [samples, setSamples] = useState([])
  
 // Sample data processing
useEffect(() => {
  if (!sampleData?.samples?.edges) return;

  const processedData = sampleData.samples.edges.map(edge => {
    const { sampleId, matId, data } = edge.node;
    // Parse the data string into an object
    const parsedData = JSON.parse(data);
    parsedData["onset_potential_(+/-100_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.1_mA/cm2)"]).toFixed(2);
    parsedData["onset_potential_(+/-1000_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-1_mA/cm2)"]).toFixed(2);
    parsedData["onset_potential_(+/-10,000_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-10_mA/cm2)"]).toFixed(2);
    parsedData["onset_potential_(+/-50_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.05_mA/cm2)"]).toFixed(2);
    parsedData["onset_potential_(+/-10_\u03BCA/cm2)"] =  Number(parsedData["onset_potential(+/-0.01_mA/cm2)"]).toFixed(2);

    delete parsedData["onset_potential(+/-0.1_mA/cm2)"];
    delete parsedData["onset_potential(+/-1_mA/cm2)"];
    delete parsedData["onset_potential(+/-10_mA/cm2)"];
    delete parsedData["onset_potential(+/-0.05_mA/cm2)"];
    delete parsedData["onset_potential(+/-0.01_mA/cm2)"];
    
    parsedData.sampleId = sampleId;
    parsedData.matId = matId;
    const sortedParsedData = Object.fromEntries(Object.entries(parsedData).sort(([a], [b]) => a.localeCompare(b)));

    // Create an ordered object based on defaultVisibleColumns
    const orderedData = {};
    // Add sampleId and matId
    

    // First add the keys from defaultVisibleColumns in specified order
    defaultVisibleColumns.forEach(key => {
      orderedData[key] = sortedParsedData[key];
    });
    
    
    
    // Add remaining keys that are not in defaultVisibleColumns
    Object.keys(sortedParsedData).forEach(key => {
      if (!defaultVisibleColumns.includes(key)) {
        orderedData[key] = sortedParsedData[key];
      }
    });
    
    return orderedData;
  });

  setSamples(processedData);
}, [sampleData]);

// XRD and XPS data fetching
const fetchXrdAndXpsData = useCallback(async () => {
  if (!samples.length || !selectedRowIndices.length) return;

  try {
    const selectedSamples = selectedRowIndices.map(index => filterSamples[index]);
    
    const [xrdResults, xpsResults] = await Promise.all([
      Promise.all(selectedSamples.map(sample => 
        getXrdData({ variables: { matId: parseInt(sample.matId) } })
      )),
      Promise.all(selectedSamples.map(sample => 
        getXpsData({ variables: { matId: parseInt(sample.matId) } })
      ))
    ]);

    // Process XRD data with normalized intensity
    const processedXrdData = xrdResults.flatMap((result, index) =>
      result.data?.xrd?.edges.map(({ node }) => {
        // Find max intensity for normalization
        const maxIntensity = Math.max(...node.intensity);
        const minIntensity =  Math.min(...node.intensity);
      
        // Create normalized intensity array
        const normalizedIntensity = node.intensity.map(value => 
          (value - minIntensity) / maxIntensity
        );
        
        return {
          degree: node.degree,
          intensity: normalizedIntensity,
          material: (selectedRowIndices[index]+1) + ". "+ selectedSamples[index].composition
        };
      }) || []
    );

    // Process XPS data with normalized intensity
    const processedXpsData = xpsResults.flatMap((result, index) =>
      result.data?.xps?.edges.map(({ node }) => {
        // Find max intensity for normalization
        const maxIntensity = Math.max(...node.intensity);
        const minIntensity =  Math.min(...node.intensity);
        
        // Create normalized intensity array
        const normalizedIntensity = node.intensity.map(value => 
          (value - minIntensity) / maxIntensity
        );
        
        return {
          bindingEnergy: node.bindingEnergy,
          intensity: normalizedIntensity,
          material: (selectedRowIndices[index]+1) + ". "+ selectedSamples[index].composition
        };
      }) || []
    );

    setXrdData(processedXrdData);
    setXpsData(processedXpsData);
  } catch (error) {
    console.error('Error fetching XRD/XPS data:', error);
  }
}, [samples, selectedRowIndices, getXrdData, getXpsData]);

// CV data fetching
const fetchCVData = useCallback(async () => {
  if (!samples.length || !selectedRowIndices.length) return;

  try {
    const selectedSamples = selectedRowIndices.map(index => filterSamples[index]);
    
    const cvResults = await Promise.all(
      selectedSamples.map(sample =>
        getEchemicalData({ variables: { sampleId: parseInt(sample.sampleId, 10) } })
      )
    );

    const initialCVData = [];
    const endCVData = [];

    cvResults.forEach((result, index) => {
      const sample = selectedSamples[index];
      if (result?.data?.echemical?.edges) {
        result.data.echemical.edges.forEach(({ node }) => {
      const sample = selectedSamples[index];
          const min_value = Math.min(node.potential);
          const min_value_index = node.potential.indexOf(min_value);
          var max_value = Math.min(node.potential);
          const max_value_index = node.potential.indexOf(max_value);
          var local_max_min_index = (node.current.length)/2;
          var firstPartValue = "Forward sweep";
          var secondaPartValue = "Reverse sweep";
          if(min_value < node.potential[0] && min_value < node.potential[node.potential.length-1]){
            local_max_min_index = min_value_index;
            firstPartValue = "Reverse sweep";
            secondaPartValue = "Forward sweep";
          }  
          
          if(max_value > node.potential[0] && max_value > node.potential[node.potential.length-1]){
            local_max_min_index = max_value_index;
            firstPartValue = "Forward sweep";
            secondaPartValue = "Reverse sweep";
          } 

          const cvData = { ...node, material: (selectedRowIndices[index]+1) + ". "+ sample.composition, mid_index: local_max_min_index, firstPartName: "Forward Sweep",
            secondPartName: "Reverse Sweep" };
          if (cvData.cvtype === "CV_initial") {
            initialCVData.push(cvData);
          } else {
            endCVData.push(cvData);
          }
        });
      }
    });

    setCvInitialData(initialCVData);
    setCvEndData(endCVData);
  } catch (error) {
    console.error('Error fetching CV data:', error);
  }
}, [samples, selectedRowIndices, getEchemicalData]);

// Effects for data fetching
useEffect(() => {
  if (!loading && !error && selectedRowIndices.length > 0) {
    fetchXrdAndXpsData();
    fetchCVData();
  }
}, [loading, error, selectedRowIndices, fetchXrdAndXpsData, fetchCVData]);

// Filtered samples
const filterSamples = useMemo(() =>
  samples.filter(sample => {
    if (selectedElements.length === 0 && !filters.space_group && !filters.reaction) return true;
    
    let matches = true;
    
    // Filter by selected elements
    if (selectedElements.length > 0) {
      if (!sample?.composition) return false;
      matches = matches && selectedElements.every(element =>
        sample.composition.includes(element)
      );
    }
    
    // Filter by space group
    if (filters.space_group) {
      matches = matches && sample.space_group === filters.space_group;
    }
    
    // Filter by reaction
    if (filters.reaction) {
      matches = matches && sample.reaction === filters.reaction;
    }
    return matches;
  }),
  [samples, selectedElements, filters]
);
  // Event handlers
  const handleElementClick = useCallback((element) => {
    setSelectedElements(prev =>
      prev.includes(element) ? prev.filter(e => e !== element) : [...prev, element]
    );
    setSearchQuery(prev => {
      const elements = prev.split('-').filter(e => e !== element);
      return prev.split('-').includes(element) 
        ? elements.join('-') 
        : [...elements, element].join('-');
    });
  }, []);

  const handleSearch = useCallback(() => {
    setIsPeriodicTableVisible(false);
    console.log("Searching for:", searchQuery);
  }, [searchQuery]);

  const handleFilterChange = useCallback((e) => {
    setFilters(prev => ({ ...prev, [e.target.name]: e.target.value }));
  }, []);

  const handleSelectedRowsChange = useCallback((selectedIndices) => {
    setSelectedRowIndices(selectedIndices);
  }, []);

  // Element class utility
  const getElementClass = (symbol) => {
    if (!symbol) return "w-14 h-14";
    let baseClass = "w-14 h-14 text-sm flex items-center justify-center border border-gray-300";
    if (selectedElements.includes(symbol)) {
      return `${baseClass} bg-red-800 text-white`;
    }
    if (searchQuery && symbol.toLowerCase().includes(searchQuery.toLowerCase())) {
      return `${baseClass} bg-yellow-300`;
    }
    switch (periodicTableData[symbol]?.group) {
      case 'alkali': return `${baseClass} bg-green-200`;
      case 'alkaline': return `${baseClass} bg-yellow-200`;
      case 'transition': return `${baseClass} bg-blue-200`;
      case 'post-transition': return `${baseClass} bg-red-200`;
      case 'metalloid': return `${baseClass} bg-orange-200`;
      case 'nonmetal': return `${baseClass} bg-purple-200`;
      case 'noble': return `${baseClass} bg-pink-200`;
      case 'lanthanide': return `${baseClass} bg-yellow-300`;
      case 'actinide': return `${baseClass} bg-orange-300`;
      default: return `${baseClass} bg-gray-200`;
    }
  };

  // Render functions
  const renderElement = (symbol) => {
    if (!symbol) return <div className="w-14 h-14"></div>;
    return (
      <button
        key={symbol}
        className={getElementClass(symbol)}
        onClick={() => handleElementClick(symbol)}
      >
        {symbol}
      </button>
    );
  };

  const renderSearchBar = () => (
    <div className="mb-4">
      <p className="text-sm text-gray-600 mb-2">Search Material Data by Chemical Composition or Properties.</p>
      <div className="flex items-stretch gap-2">
        <div className="flex-grow flex items-stretch">
          <div className="bg-gray-100 px-4 py-2 flex items-center rounded-l border border-r-0 border-gray-300">
            <span className="text-gray-700">Materials</span>
          </div>
          <input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Enter elements (e.g., Mn-Co)"
            className="flex-grow px-3 py-2 border border-gray-300 focus:outline-none focus:border-gray-400"
          />
        </div>
        
        <button
          onClick={togglePeriodicTable}
          className="px-3 flex items-center justify-center bg-white border border-gray-300 hover:bg-red-800 transition-colors"
          title="Toggle Periodic Table"
        >
          <svg 
            viewBox="0 0 24 24" 
            className="w-6 h-6" 
            fill="none" 
            stroke="currentColor" 
            strokeWidth="2"
          >
            <path d="M4 4h4v4H4zM10 4h4v4h-4zM16 4h4v4h-4zM4 10h4v4H4zM10 10h4v4h-4zM16 10h4v4h-4zM4 16h4v4H4zM10 16h4v4h-4zM16 16h4v4h-4z" />
          </svg>
        </button>
  
        <button
          onClick={handleSearch}
          className="px-6 py-2 bg-red-800 text-white rounded hover:bg-teal-600 transition-colors"
        >
          Search
        </button>
      </div>
    </div>
  );

  const renderPeriodicTable = () => {
    const table = [];
    for (let period = 1; period <= 7; period++) {
      const row = [];
      for (let column = 1; column <= 18; column++) {
        const element = Object.entries(periodicTableData).find(([symbol, data]) => {
          // If we are in period 7 and column 3-17, only include non-actinides
          if (period === 7 && column >= 3 && column <= 17) {
            return data.period === period && data.column === column && data.group !== 'actinide';
          }
          // If we are in period 6 column 3, skip La (lanthanide)
          if (period === 6 && column === 3) {
            return false; // Skip La in main table
          }
          // For all other positions, include all elements
          return data.period === period && data.column === column;
        });
        row.push(renderElement(element ? element[0] : null));
      }
      table.push(<div key={period} className="flex justify-center">{row}</div>);
    }

    const lanthanides = Object.entries(periodicTableData)
      .filter(([_, data]) => data.group === 'lanthanide')
      .map(([symbol, _]) => renderElement(symbol));
    const actinides = Object.entries(periodicTableData)
      .filter(([_, data]) => data.group === 'actinide')
      .map(([symbol, _]) => renderElement(symbol));

    // table.push(<div key="lanthanides" className="flex justify-end mt-4 -mr-40">{lanthanides}</div>);
    // table.push(<div key="actinides" className="flex justify-end  -mr-40 ">{actinides}</div>);
    table.push(<div key="lanthanides" className="flex justify-end mt-4" style={{ marginRight: '-161px' }}>{lanthanides}</div>);
    table.push(<div key="actinides" className="flex justify-end" style={{ marginRight: '-161px' }}>{actinides}</div>);

    return (
      <div 
        className={`mb-4 bg-white rounded-lg shadow overflow-hidden transition-all duration-300 ease-in-out ${
          isAnimating ? 'animate-slide' : ''
        }`}
        style={{
          maxHeight: isPeriodicTableVisible ? '2000px' : '0',
          opacity: isPeriodicTableVisible ? 1 : 0,
          padding: isPeriodicTableVisible ? '1rem' : '0',
          marginBottom: isPeriodicTableVisible ? '1rem' : '0',
        }}
      >
        <div className="flex space-x-2 mb-4">
          {['Contain Elements'].map(mode => (
            <Button
              key={mode}
              onClick={() => setSearchMode(mode)}
              className={`${searchMode === mode ? 'bg-red-800' : 'bg-gray-200 text-gray-700'}`}
            >
              {mode}
            </Button>
          ))}
        </div>
        <div className="periodic-table flex flex-col items-center">
          {table}
        </div>
      </div>
    );
  };

  
  


  
  const renderFilters = () => (
    <div className="mb-4 flex space-x-4">
      {/* <select
        name="space_group"
        onChange={handleFilterChange}
        value={filters.space_group}
        className="p-2 border rounded bg-white"
      >
        <option value="">All Space Groups</option>
        {[...new Set(samples.map(m => m.space_group))]  // Changed from spaceGroup to space_group
          .filter(group => group) // Remove null/undefined values
          .sort()  // Sort the space groups alphabetically
          .map(group => (
            <option key={group} value={group}>{group}</option>
          ))}
      </select> */}

      <select
        name="reaction"
        onChange={handleFilterChange}
        value={filters.reaction || ''}
        className="p-2 border rounded bg-white"
      >
        <option value="">All Reactions</option>
        {[
          'OER',
          'HER',
          'ORR',
        ].map(reaction => (
          <option key={reaction} value={reaction}>{reaction}</option>
        ))}
      </select>
    </div>
  );

  const renderMaterialsTable = () => (
    loading ? (
      <p className="text-center">Loading...</p>
    ) : error ? (
      <p className="text-center text-red-600">Error: {error.message}</p>
    ) : (
      // <MaterialsTable
      //   data={filterSamples}
      //   defaultVisibleColumns={defaultVisibleColumns}
      //   onSelectedRowsChange={handleSelectedRowsChange}
      // />

      <AdvanceMaterialTable data={filterSamples} defaultVisibleColumns={defaultVisibleColumns} onSelectedRowsChange={handleSelectedRowsChange}></AdvanceMaterialTable>

    )
  );


  const renderGraphs = () => (
    !loading && !error && (
      <div>
        <h3 className="text-2xl font-bold mb-6 mt-12">Characterization spectra</h3>
        <div className="text-lg mb-6">
        X-ray diffraction (XRD) and photoelectron spectroscopy (XPS) results are shown below.
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
            <GenericGraph
              data={xrdData}
              title="XRD"
              xAxisTitle="2 theta (degree)"
              yAxisTitle="Intensity"
              xKey="degree"
              yKey="intensity"
              nameKey="material"
            />
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
            <GenericGraph
              data={xpsData}
              title="XPS"
              xAxisTitle="Binding energy (eV)"
              yAxisTitle="Intensity"
              xKey="bindingEnergy"
              yKey="intensity"
              nameKey="material"
            />
          </div>
        </div>

        <h3 className="text-2xl font-bold mb-4">Electrochemical testing</h3>
        {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
            <GenericGraph
              data={cvInitialData}
              title="CV (initial)"
              xAxisTitle="Potential (V)"
              yAxisTitle="Current (mA/cm²)"
              xKey="potential"
              yKey="current"
              nameKey="material"
              showIntersection={true}
              initialIntersectY={-0.1}
            />
          </div>
          <div className="bg-white p-4 rounded-lg shadow-md h-[500px]">
            <GenericGraph
              data={cvEndData}
              title="CV (end)"
              xAxisTitle="Potential (V)"
              yAxisTitle="Current (mA/cm²)"
              xKey="potential"
              yKey="current"
              nameKey="material"
            />
          </div>
        </div> */}
        <div className="text-lg mb-6">
        Cyclic voltametry (CV) curves are shown below. Select the current density value in the legend below to re-calculate the onset potentials.
        </div>
        <div className="max-w-6xl mx-auto mb-8">
          <div className="bg-white p-4 rounded-lg shadow-md w-[800px] h-[600px] mx-auto">
            <GenericGraph
              data={cvInitialData}
              title="CV curve"
              xAxisTitle="Potential (V vs. RHE)"
              yAxisTitle="Current (mA/cm²)"
              xKey="potential"
              yKey="current"
              nameKey="material"
              showIntersection={true}
              initialIntersectY={-0.1}
              showLegend={false}
            />
          </div>
        </div>

      </div>
    )
  );

  return (
    <div className="flex flex-col min-h-screen">
      <nav className="bg-white shadow-md">
        <div className=" px-4 py-2 flex justify-between items-center">
          <Header />
          <div className="text-red-800 font-bold">Material/Sample Search</div>
          <img src="/images/catalysis-hub-logo.png" alt="Catalysis-Hub Logo" className="w-40 h-16 object-contain" />
        </div>
      </nav>

      <style jsx global>{`
        @keyframes slideUp {
          from { 
            max-height: 2000px; 
            opacity: 1; 
            padding: 1rem;
            margin-bottom: 1rem;
          }
          to { 
            max-height: 0; 
            opacity: 0; 
            padding: 0;
            margin-bottom: 0;
          }
        }
        @keyframes slideDown {
          from { 
            max-height: 0; 
            opacity: 0;
            padding: 0;
            margin-bottom: 0;
          }
          to { 
            max-height: 2000px; 
            opacity: 1;
            padding: 1rem;
            margin-bottom: 1rem;
          }
        }
        .animate-slide {
          animation: ${isPeriodicTableVisible ? 'slideDown' : 'slideUp'} 300ms ease-in-out;
        }
      `}</style>

      <div className="flex-grow bg-gray-100">
        <div className="container mx-auto p-4">
          <h1 className="text-3xl font-bold mb-4">Explore Catalyst Materials</h1>
          {renderSearchBar()}
          {renderPeriodicTable()}
          {renderFilters()}
          {renderMaterialsTable()}
          {renderGraphs()}        
        </div>
      </div>

      <footer className="bg-red-800 text-white py-4">
        <div className="container mx-auto px-4 flex justify-between items-center">
          <div className="flex items-center">
            <img src="/images/suncat_logo.png" alt="suncat_logo.png" className="h-8 mr-4" />
          </div>
          <nav>
            <ul className="flex space-x-6 items-center">
              {[
                { icon: FaBook, text: 'Docs' },
                { icon: FaGithub, text: 'Visit us on GitHub' },
                { icon: FaCreativeCommons, text: 'License' }
              ].map(({ icon: Icon, text }) => (
                <li key={text}>
                  <a href="#" className="flex items-center hover:underline">
                    <Icon className="mr-2" /> {text}
                  </a>
                </li>
              ))}
            </ul>
          </nav>
        </div>
      </footer>
    </div>
  );
};

export default ExploreMaterialsSearch;